import React, { useState, useEffect } from 'react';
import { Calendar, ExternalLink } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import RotatingText from '../components/common/RotatingText';

const GolfRSANewsPage = () => {
  const { isDarkMode } = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRSSFeed();
  }, []);

  const fetchRSSFeed = async () => {
    try {
      setLoading(true);
      // Using a CORS proxy to fetch the RSS feed
      const response = await fetch('https://api.rss2json.com/v1/api.json?rss_url=https://golfrsa.com/feed');
      const data = await response.json();
      
      if (data.status === 'ok') {
        setPosts(data.items);
      } else {
        setError('Failed to load news');
      }
    } catch (err) {
      console.error('Error fetching RSS:', err);
      setError('Failed to load news feed');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getExcerpt = (content, length = 200) => {
    const text = stripHtml(content);
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          {/* Mobile Image */}
          <img 
            src="/news-mobile.png" 
            alt="GolfRSA News" 
            className="md:hidden w-full h-full object-cover"
          />
          {/* Desktop Image */}
          <img 
            src="/news.png" 
            alt="GolfRSA News" 
            className="hidden md:block w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-fsgu-gray-900/60 via-fsgu-gray-900/70 to-fsgu-gray-900/90' 
              : 'bg-gradient-to-b from-black/40 via-black/50 to-black/70'
          }`}></div>
        </div>

        {/* Content */}
        <div className="container-custom px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-cursive mb-8 md:mb-12 text-white leading-tight tracking-tight">
  National News
</h1>
            
            {/* Rotating Text Section */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light mb-8">
              <span className="drop-shadow-lg text-white">Latest from</span>
              <RotatingText
                texts={[
                  'SA Golf',
                  'Tournaments',
                  'Players',
                  'Development',
                  'Juniors',
                ]}
                mainClassName={`font-cursive font-normal px-6 py-3 pb-4 rounded-2xl ${
                  isDarkMode
                    ? 'bg-fsgu-green-700/95 text-white'
                    : 'bg-fsgu-green-700 text-white'
                } drop-shadow-2xl`}
                splitLevelClassName="overflow-visible"
                staggerFrom="first"
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '-120%', opacity: 0 }}
                staggerDuration={0.03}
                transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                rotationInterval={3000}
              />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Stay updated with GolfRSA
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <div className="animate-bounce">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* News Content Section */}
      <section className="section">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-fsgu-green-500"></div>
              <p className={`mt-4 ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-600'}`}>
                Loading news...
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className={`text-lg ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-600'}`}>
                {error}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, index) => (
                <article key={index} className="card-hover group">
                  {/* Image */}
                  {post.thumbnail && (
                    <div className="aspect-video overflow-hidden rounded-t-lg mb-4">
                      <img
                        src={post.thumbnail}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Date */}
                  <div className="flex items-center text-sm mb-3">
                    <Calendar
                      className={`w-4 h-4 mr-2 ${
                        isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                      }`}
                    />
                    <span
                      className={
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }
                    >
                      {formatDate(post.pubDate)}
                    </span>
                  </div>

                  {/* Title */}
                  <h2
                    className={`text-xl font-semibold mb-3 line-clamp-2 ${
                      isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                    }`}
                  >
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  <p
                    className={`text-sm mb-4 line-clamp-3 ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}
                  >
                    {getExcerpt(post.description || post.content)}
                  </p>

                  {/* Read More Link */}
                  <a
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center text-sm font-medium transition-colors duration-300 ${
                      isDarkMode
                        ? 'text-fsgu-green-400 hover:text-fsgu-green-300'
                        : 'text-fsgu-green-500 hover:text-fsgu-green-600'
                    }`}
                  >
                    Read on GolfRSA
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </article>
              ))}
            </div>
          )}

          {/* Visit GolfRSA Button */}
          {!loading && !error && (
            <div className="text-center mt-12">
              <a
                href="https://golfrsa.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary inline-flex items-center"
              >
                Visit GolfRSA Website
                <ExternalLink className="w-5 h-5 ml-2" />
              </a>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default GolfRSANewsPage;